export const DEFAULT_IGNORED_SENTRY_MESSAGES = [
  'request aborted',
  'network error',
  'request failed',
  'error while fetching',
  'captured as promise rejection',
  'unknown',
  'redundant navigation',
  'find variable: gmo',
  'find variable: webkit',
  'the string did not match the expected pattern',
  'loading css chunk',
  'error: redirected when going from',
  'redirected when going from',
  'navigation cancelled from',
  '_leaflet_pos',
  'java object is gone',
  'aborterror: the operation was aborted.',
  'aborterror: signal is aborted without reason',
  'signal is aborted without reason',
  'the user aborted a request.',
  'invalid call to runtime.sendmessage(). tab not found.',
  "undefined is not an object (evaluating 'window.webkit.messagehandlers')",
];

const EXTENSION_PREFIXES = ['chrome-extension://', 'moz-extension://', 'safari-extension://', 'file://'];
const ANONYMOUS_FRAME_NAMES = ['', '?', '<anonymous>', 'anonymous', 'undefined'];
const THIRD_PARTY_SNIPPETS = [
  'leaflet',
  'google-analytics.com',
  'hs-analytics',
  'hs-scripts',
  'hscollectedforms',
  'posthog-js',
  'beacon.min.js',
  '/x-plugin-script/',
  '/_/translate_http/_/js/',
];
const APP_SOURCE_SNIPPETS = ['orq-web/', 'webpack://', '/js/', '/css/'];
const RAW_ROUTE_FRAME_PATTERN = /^\/[^.?#]+(?:[?#].*)?$/;
const MINIFIED_FUNCTION_FRAME_PATTERN = /^[a-z_$][a-z0-9_$]?$/i;

function normalize(value) {
  return value ? value.toString().toLowerCase() : '';
}

export function getSentryOriginalMessage(hint) {
  return normalize(hint && hint.originalException && hint.originalException.message);
}

export function getSentryOriginalStack(hint) {
  return normalize(hint && hint.originalException && hint.originalException.stack);
}

export function getSentryExceptionValue(event) {
  const values = event && event.exception && event.exception.values;
  if (!values || values.length !== 1 || !values[0].value) {
    return '';
  }
  return normalize(values[0].value);
}

export function getSentryFrames(event) {
  const values = event && event.exception && event.exception.values;
  if (!values || values.length !== 1) {
    return [];
  }
  return (values[0].stacktrace && values[0].stacktrace.frames) || [];
}

export function isIgnoredMessage(message, ignoreList = DEFAULT_IGNORED_SENTRY_MESSAGES) {
  const normalizedMessage = normalize(message);
  return ignoreList.some(ignore => normalizedMessage.includes(ignore));
}

export function isBrowserActionPuppeteerError(message, stack) {
  const normalizedMessage = normalize(message);
  const normalizedStack = normalize(stack);
  return (
    normalizedMessage.includes('cannot redefine property: browseraction') &&
    (normalizedStack.includes('pptr:evaluate') || normalizedStack.includes('file:///home/pptruser/cli.mjs'))
  );
}

export function hasIgnoredFrameFilename(frames) {
  return frames.some(f => {
    const filename = normalize(f.filename);
    return (
      EXTENSION_PREFIXES.some(prefix => filename.startsWith(prefix)) ||
      THIRD_PARTY_SNIPPETS.some(snippet => filename.includes(snippet))
    );
  });
}

export function hasOnlyAnonymousFrames(frames) {
  return Boolean(frames.length) && frames.every(f => ANONYMOUS_FRAME_NAMES.includes(normalize(f.filename)));
}

export function hasAppSourceFrame(frames) {
  return frames.some(f => {
    const filename = normalize(f.filename);
    return APP_SOURCE_SNIPPETS.some(snippet => filename.includes(snippet));
  });
}

export function isRawRouteMinifiedScriptError({message, value, frames}) {
  const normalizedMessage = normalize(message);
  const normalizedValue = normalize(value);
  const hasRawRouteFrame = frames.some(f => RAW_ROUTE_FRAME_PATTERN.test(normalize(f.filename)));
  const hasMinifiedFunctionFrame = frames.some(f => {
    const functionName = normalize(f.function);
    return ANONYMOUS_FRAME_NAMES.includes(functionName) || MINIFIED_FUNCTION_FRAME_PATTERN.test(functionName);
  });
  return (
    !hasAppSourceFrame(frames) &&
    hasRawRouteFrame &&
    hasMinifiedFunctionFrame &&
    (normalizedMessage.includes('maximum call stack size exceeded') ||
      normalizedMessage === 'aa' ||
      normalizedValue === 'aa')
  );
}

export function isGoogleMapsNoise(message, frames) {
  const normalizedMessage = normalize(message);
  const isGoogleMaps = frames.some(f => normalize(f.filename).includes('/maps-api-v3/api/js/'));
  return (
    (isGoogleMaps && normalizedMessage.includes('htmldialogelement')) || normalizedMessage.includes('could not load')
  );
}

export function isStaticChunkHtmlSyntaxError(message, frames) {
  const normalizedMessage = normalize(message);
  if (!normalizedMessage.includes("unexpected token '<'")) {
    return false;
  }
  return frames.some(f => {
    const filename = normalize(f.filename);
    return filename.includes('/static/js/') && /\.js(\?|$)/.test(filename);
  });
}

export function shouldIgnoreSentryEvent(event, hint, ignoreList = DEFAULT_IGNORED_SENTRY_MESSAGES) {
  const message = getSentryOriginalMessage(hint);
  if (!message) {
    return true;
  }

  const stack = getSentryOriginalStack(hint);
  if (isBrowserActionPuppeteerError(message, stack) || isIgnoredMessage(message, ignoreList)) {
    return true;
  }

  const value = getSentryExceptionValue(event);
  if (!value) {
    return false;
  }
  if (isIgnoredMessage(value, ignoreList)) {
    return true;
  }

  const frames = getSentryFrames(event);
  return (
    hasIgnoredFrameFilename(frames) ||
    hasOnlyAnonymousFrames(frames) ||
    isRawRouteMinifiedScriptError({message, value, frames}) ||
    isStaticChunkHtmlSyntaxError(message, frames) ||
    isGoogleMapsNoise(message, frames)
  );
}
